import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Truck, Sparkles } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const KINDS = [
  { value: 'flat', label: 'Flat (added once)' },
  { value: 'per_km', label: 'Per kilometer (× distance)' },
];

const DeliveryFeesPage = () => {
  const [data, setData] = useState({ components: [], previews: [], platform_commission_pct: 10, service_fee_flat_cad: 1.0, service_fee_pct: 5.0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/delivery-fees').then((res) => res.data);
      setData(r);
    } catch (e) {
      toast.error('Failed to load delivery fees');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = (idx, patch) => {
    const next = [...data.components];
    next[idx] = { ...next[idx], ...patch };
    setData({ ...data, components: next });
  };

  const remove = (idx) => {
    const next = data.components.filter((_, i) => i !== idx);
    setData({ ...data, components: next });
  };

  const add = (preset) => {
    const presets = {
      base: { key: 'base', label: 'Base fee', kind: 'flat', price_cad: 3.49 },
      per_km: { key: 'per_km', label: 'Per kilometer', kind: 'per_km', price_cad: 0.65 },
      priority: { key: 'priority', label: 'Priority delivery', kind: 'flat', price_cad: 4.99 },
    };
    setData({ ...data, components: [...data.components, presets[preset] || { key: `c${data.components.length + 1}`, label: 'New fee', kind: 'flat', price_cad: 1.00 }] });
  };

  const save = async () => {
    // basic validation
    const keys = new Set();
    for (const c of data.components) {
      if (!c.key) { toast.error('Each component needs a key'); return; }
      if (keys.has(c.key)) { toast.error(`Duplicate key: ${c.key}`); return; }
      keys.add(c.key);
      if (!c.label) { toast.error('Each component needs a label'); return; }
      if (c.price_cad < 0) { toast.error('Price cannot be negative'); return; }
    }
    setSaving(true);
    try {
      const r = await api.put('/admin/delivery-fees', {
        components: data.components.map((c) => ({ ...c, price_cad: Number(c.price_cad) })),
        platform_commission_pct: Number(data.platform_commission_pct),
        service_fee_flat_cad: Number(data.service_fee_flat_cad),
        service_fee_pct: Number(data.service_fee_pct),
      }).then((res) => res.data);
      setData(r);
      toast.success('Delivery fees saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div data-testid="delivery-fees-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Truck className="h-6 w-6 text-emerald-600" /> Delivery fee structure
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl">
            Configure how delivery fees are calculated. All prices are stored in CAD — they’re
            automatically converted to each vendor’s local currency at checkout. Platform payouts
            via Stripe remain in CAD.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-delivery-fees-btn">
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      <Card className="mt-6 border-slate-200">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Components (CAD)</div>
          <div className="mt-3 space-y-3">
            {data.components.map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 items-end p-3 rounded-xl bg-slate-50 border border-slate-200" data-testid={`fee-row-${i}`}>
                <div className="col-span-12 sm:col-span-2">
                  <Label className="text-xs">Key</Label>
                  <Input value={c.key} onChange={(e) => update(i, { key: e.target.value.toLowerCase().replace(/\s/g, '_') })} className="h-9 mt-1" data-testid={`fee-key-${i}`} />
                </div>
                <div className="col-span-12 sm:col-span-3">
                  <Label className="text-xs">Label</Label>
                  <Input value={c.label} onChange={(e) => update(i, { label: e.target.value })} className="h-9 mt-1" data-testid={`fee-label-${i}`} />
                </div>
                <div className="col-span-7 sm:col-span-3">
                  <Label className="text-xs">Type</Label>
                  <select value={c.kind} onChange={(e) => update(i, { kind: e.target.value })}
                    className="h-9 mt-1 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`fee-kind-${i}`}>
                    {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                </div>
                <div className="col-span-3 sm:col-span-2">
                  <Label className="text-xs">Price (CAD)</Label>
                  <Input type="number" step="0.01" value={c.price_cad}
                    onChange={(e) => update(i, { price_cad: e.target.value })} className="h-9 mt-1" data-testid={`fee-price-${i}`} />
                </div>
                <div className="col-span-2 sm:col-span-2 flex justify-end">
                  <Button variant="outline" size="sm" onClick={() => remove(i)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`fee-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => add('base')} data-testid="add-base-fee"><Plus className="h-4 w-4 mr-1" />Base fee</Button>
            <Button variant="outline" size="sm" onClick={() => add('per_km')} data-testid="add-per-km-fee"><Plus className="h-4 w-4 mr-1" />Per-km fee</Button>
            <Button variant="outline" size="sm" onClick={() => add('priority')} data-testid="add-priority-fee"><Plus className="h-4 w-4 mr-1" />Priority delivery</Button>
            <Button variant="outline" size="sm" onClick={() => add('custom')} data-testid="add-custom-fee"><Plus className="h-4 w-4 mr-1" />Custom</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-5 border-slate-200">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Service fee (ShipitNow)</div>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Charged to the customer on every order on top of the delivery fee — always ShipitNow&rsquo;s take,
            never the vendor&rsquo;s. The fee is <b>whichever is higher</b>: the flat amount or the percentage of
            the goods subtotal. Configured in CAD and converted to the vendor&rsquo;s currency at checkout
            (CA$ in Canada, ₦ in Nigeria).
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
            <div>
              <Label className="text-xs">Flat service fee (CAD)</Label>
              <Input type="number" step="0.25" min="0"
                value={data.service_fee_flat_cad}
                onChange={(e) => setData({ ...data, service_fee_flat_cad: e.target.value })}
                className="h-9 mt-1" data-testid="service-fee-flat-input" />
            </div>
            <div>
              <Label className="text-xs">% of goods subtotal</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" step="0.5" min="0" max="50"
                  value={data.service_fee_pct}
                  onChange={(e) => setData({ ...data, service_fee_pct: e.target.value })}
                  className="h-9 w-24 text-right" data-testid="service-fee-pct-input" />
                <span className="text-slate-500 text-sm">%</span>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2" data-testid="service-fee-example">
            Example: on a CA$40 basket, fee = max(CA${Number(data.service_fee_flat_cad || 0).toFixed(2)}, {Number(data.service_fee_pct || 0)}% = CA${((40 * Number(data.service_fee_pct || 0)) / 100).toFixed(2)}).
          </p>
        </CardContent>
      </Card>

      <Card className="mt-5 border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Platform commission</div>
              <p className="text-sm text-slate-500 mt-1">Percentage of order subtotal collected as platform fee on Stripe Connect.</p>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.5" min="0" max="50"
                value={data.platform_commission_pct} onChange={(e) => setData({ ...data, platform_commission_pct: e.target.value })}
                className="h-9 w-24 text-right" data-testid="platform-commission-input" />
              <span className="text-slate-500 text-sm">%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-5 border-slate-200 bg-gradient-to-br from-emerald-50 to-white">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-semibold">
            <Sparkles className="h-4 w-4" /> Live preview (sample distances)
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            {(data.previews || []).map((p) => (
              <div key={p.distance_km} className="rounded-xl bg-white border border-emerald-200 p-4">
                <div className="text-xs text-slate-500">{p.distance_km} km</div>
                <div className="text-xl font-semibold text-emerald-700 mt-1">CA${p.total_cad.toFixed(2)}</div>
                {p.total_ngn != null && (
                  <div className="text-sm font-medium text-slate-500 mt-0.5" data-testid={`preview-ngn-${p.distance_km}`}>₦{Number(p.total_ngn).toLocaleString()}</div>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Fees are configured in CAD. At checkout, Canadian transactions are charged in <b>CA$</b> and Nigerian
            transactions in <b>₦</b> (converted with live FX rates, cached 24h) — no other currencies are used.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeliveryFeesPage;
